// ============================================================
//  PrintReport.jsx — تقرير الأداء الشامل بشعار قادر
// ============================================================
import React from 'react';
import { LogoIcon } from './Logo';

const PLAN_NAMES = {
  basic: 'الأساسية ⭐',
  pro:   'المتقدمة 🚀',
  elite: 'الشاملة 🌍',
};

const MODE_LABELS = {
  'كمي':    { label: 'القدرات الكمي',    color: '#3b82f6' },
  'لفظي':   { label: 'القدرات اللفظي',   color: '#7c6df8' },
  'تحصيلي': { label: 'التحصيلي',         color: '#10b981' },
  'step':   { label: 'STEP',             color: '#f59e0b' },
};

function ProgressBar({ pct, color }) {
  return (
    <div style={{ background: '#e5e7eb', borderRadius: 6, height: 10, flex: 1, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 6 }} />
    </div>
  );
}

export default function PrintReport({ user, plan, perf, sub, sessions, onClose }) {
  const today = new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' });
  const planId = plan?.id || plan?.name_ar || 'basic';
  const planName = PLAN_NAMES[planId] || planId;
  const phone = user?.phone?.replace(/^966/, '0') || '—';

  const expires = sub?.expires_at
    ? new Date(sub.expires_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  const totalQ    = perf?.total_questions || 0;
  const avgScore  = perf?.avg_score       || 0;
  const totalSess = perf?.total_sessions  || 0;
  const byMode    = perf?.by_mode         || {};

  const handleShare = () => {
    const lines = [];
    lines.push('📊 *تقرير أدائي على منصة قادر*');
    lines.push('━━━━━━━━━━━━━━━━━━━');
    lines.push(`📅 التاريخ: ${today}`);
    lines.push(`🎓 الباقة: ${planName}`);
    lines.push('');
    lines.push('📌 *ملخص الأداء:*');
    lines.push(`✅ إجمالي الأسئلة: ${totalQ.toLocaleString()}`);
    lines.push(`🎯 دقة الإجابات: ${avgScore}%`);
    lines.push(`🔁 جلسات التدريب: ${totalSess}`);

    const modeEntries = Object.entries(byMode);
    if (modeEntries.length > 0) {
      lines.push('');
      lines.push('📈 *الأداء التفصيلي:*');
      modeEntries.forEach(([key, d]) => {
        const label = MODE_LABELS[key]?.label || key;
        const bar = '█'.repeat(Math.round(d.accuracy / 10)) + '░'.repeat(10 - Math.round(d.accuracy / 10));
        lines.push(`• ${label}: ${bar} ${d.accuracy}%`);
      });
    }

    lines.push('');
    lines.push('━━━━━━━━━━━━━━━━━━━');
    lines.push('🚀 ابدأ رحلتك: *qadder.sa*');

    const text = encodeURIComponent(lines.join('\n'));
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handlePrint = () => {
    const content = document.getElementById('qadder-report');
    if (!content) { window.print(); return; }

    const printWin = window.open('', '_blank', 'width=820,height=1000');
    if (!printWin) { window.print(); return; }

    printWin.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>تقرير الأداء - قادر</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800;900&display=swap" rel="stylesheet">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Tajawal', sans-serif;
      direction: rtl; color: #111827;
      padding: 24px 32px; background: #fff;
    }
    @media print {
      body { padding: 16px 24px; }
    }
  </style>
</head>
<body>
  ${content.innerHTML}
  <script>
    document.fonts.ready.then(function() {
      setTimeout(function() { window.print(); }, 400);
    });
  ${'<'}/script>
</body>
</html>`);
    printWin.document.close();
  };

  return (
    <>
      {/* ── Overlay (لا تُطبع) ── */}
      <div className="no-print" style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}>
        <div style={{
          background: '#fff', borderRadius: 16, maxWidth: 700, width: '100%',
          maxHeight: '90vh', overflowY: 'auto', position: 'relative',
        }}>
          {/* أزرار التحكم */}
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: '#f9fafb', borderRadius: '16px 16px 0 0',
          }}>
            <span style={{ fontFamily: 'Tajawal,sans-serif', fontWeight: 700, color: '#374151', fontSize: 15 }}>
              معاينة التقرير
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleShare} style={{
                background: '#25D366', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 18px', cursor: 'pointer',
                fontFamily: 'Tajawal,sans-serif', fontWeight: 700, fontSize: 14,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                شارك
              </button>
              <button onClick={handlePrint} style={{
                background: '#1a1060', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 18px', cursor: 'pointer',
                fontFamily: 'Tajawal,sans-serif', fontWeight: 700, fontSize: 14,
              }}>
                ⬇️ تحميل / طباعة
              </button>
              <button onClick={onClose} style={{
                background: '#ef4444', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                fontFamily: 'Tajawal,sans-serif', fontWeight: 700, fontSize: 14,
              }}>✕</button>
            </div>
          </div>

          {/* محتوى التقرير */}
          <div id="qadder-report" style={{ padding: '32px 36px', direction: 'rtl', fontFamily: 'Tajawal, sans-serif', color: '#111827' }}>
            <ReportContent
              today={today} phone={phone} planName={planName} expires={expires}
              totalQ={totalQ} avgScore={avgScore} totalSess={totalSess}
              byMode={byMode} sessions={sessions}
            />
          </div>
        </div>
      </div>

      <style>{`
        @media screen { .no-print { display: flex; } }
      `}</style>
    </>
  );
}

function ReportContent({ today, phone, planName, expires, totalQ, avgScore, totalSess, byMode, sessions }) {
  return (
    <>
      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg,#1a1060,#2d1b8e)',
        borderRadius: 14, padding: '24px 28px', marginBottom: 20,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <LogoIcon size={52} />
          <div>
            <div style={{ color: '#FFD700', fontSize: 32, fontWeight: 900, letterSpacing: -1 }}>قـادر</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 3 }}>
              منصة التدريب على اختبارات القدرات والتحصيلي
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>تقرير الأداء الشامل</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 3 }}>{today}</div>
        </div>
      </div>

      {/* ── معلومات المستخدم ── */}
      <Section title="معلومات المستخدم">
        <Row label="رقم الجوال" value={phone} />
        <Row label="الباقة الحالية" value={planName} />
        <Row label="تاريخ انتهاء الاشتراك" value={expires} />
      </Section>

      {/* ── ملخص الأداء ── */}
      <Section title="ملخص الأداء">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginTop: 8 }}>
          <StatBox icon="✅" value={totalQ.toLocaleString()} label="إجمالي الأسئلة" color="#22c55e" />
          <StatBox icon="🎯" value={`${avgScore}%`}          label="دقة الإجابات"   color="#7c6df8" />
          <StatBox icon="🔁" value={totalSess}               label="جلسات التدريب"  color="#f59e0b" />
        </div>
      </Section>

      {/* ── الأداء بالمادة ── */}
      {Object.keys(byMode).length > 0 && (
        <Section title="الأداء التفصيلي بالمادة">
          {Object.entries(byMode).map(([key, d]) => {
            const m = MODE_LABELS[key] || { label: key, color: '#6b7280' };
            return (
              <div key={key} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: m.color }}>{m.label}</span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    {d.sessions} جلسة &nbsp;•&nbsp; {d.questions} سؤال &nbsp;•&nbsp;
                    <strong style={{ color: m.color }}>{d.accuracy}%</strong>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ProgressBar pct={d.accuracy} color={m.color} />
                  <span style={{ fontWeight: 900, color: m.color, minWidth: 38, textAlign: 'left', fontSize: 13 }}>
                    {d.accuracy}%
                  </span>
                </div>
              </div>
            );
          })}
        </Section>
      )}

      {/* ── آخر الجلسات ── */}
      {sessions?.length > 0 && (
        <Section title="آخر الجلسات">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                {['التاريخ','النوع','الأسئلة','الصحيح','النتيجة'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.slice(0, 8).map((s, i) => {
                const score = s.score ? Math.round(s.score) : 0;
                const scoreColor = score >= 70 ? '#16a34a' : score >= 50 ? '#d97706' : '#dc2626';
                const date = s.ended_at
                  ? new Date(s.ended_at).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' })
                  : '—';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #e5e7eb', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '8px 10px' }}>{date}</td>
                    <td style={{ padding: '8px 10px' }}>{s.mode || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{s.total_questions}</td>
                    <td style={{ padding: '8px 10px' }}>{s.correct_count}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 900, color: scoreColor }}>{score}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}

      {/* ── تواصل معنا ── */}
      <div style={{
        background: 'linear-gradient(135deg,#1a1060,#2d1b8e)',
        borderRadius: 10, padding: '14px 20px', marginTop: 18,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 10,
      }}>
        <span style={{ color: '#FFD700', fontWeight: 800, fontSize: 13 }}>📬 تواصل معنا</span>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <a href="mailto:support@qadder.sa" style={{ color: '#fff', fontSize: 12, textDecoration: 'none' }}>
            📧 support@qadder.sa
          </a>
          <a href="https://qadder.sa" target="_blank" rel="noreferrer" style={{ color: '#FFD700', fontSize: 12, textDecoration: 'none', fontWeight: 700 }}>
            🔗 qadder.sa
          </a>
          <a href="https://t.me/qadder_sa" target="_blank" rel="noreferrer" style={{ color: '#229ED9', fontSize: 12, textDecoration: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="16" height="16"><circle cx="24" cy="24" r="24" fill="#229ED9"/><path fill="#fff" d="M35.2 14.3 30.9 34c-.3 1.4-1.2 1.8-2.4 1.1l-6.6-4.9-3.2 3.1c-.4.4-.6.6-1.2.6l.4-6.7 12.2-11c.5-.5-.1-.7-.8-.3L14.4 26.1l-6.6-2.1c-1.4-.4-1.5-1.4.3-2.1l25.9-10c1.2-.4 2.2.3 1.2 2.4z"/></svg>
            تيليقرام
          </a>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{
        marginTop: 12, paddingTop: 10, borderTop: '1px solid #e5e7eb',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
        <span style={{ color: '#9ca3af', fontSize: 10 }}>تم توليد هذا التقرير تلقائياً بواسطة منصة قادر</span>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        background: '#1a1060', color: '#FFD700',
        fontWeight: 800, fontSize: 14,
        padding: '7px 14px', borderRadius: '8px 8px 0 0',
      }}>
        {title}
      </div>
      <div style={{
        border: '1px solid #e5e7eb', borderTop: 'none',
        borderRadius: '0 0 8px 8px', padding: '14px 16px',
        background: '#fff',
      }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ fontWeight: 700, color: '#111827' }}>{value}</span>
    </div>
  );
}

function StatBox({ icon, value, label, color }) {
  return (
    <div style={{
      background: color + '0f', border: `1px solid ${color}33`,
      borderRadius: 10, padding: '14px 10px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
      <div style={{ color, fontSize: 22, fontWeight: 900 }}>{value}</div>
      <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>{label}</div>
    </div>
  );
}
